'use client'

import React from 'react'

import { HtmlContent } from '@portal/lib/html-content'
import { parseContent } from '@portal/news-lib/read-more'

import { PostCut } from '@/features/post-cut'
import { PostPublishingDate } from '@/features/post-publishing-date'
import { PostTag } from '@/features/post-tag'
import { PostTitleLink } from '@/features/post-title'
import { PostTranslateButton } from '@/features/post-translate-button'
import { useTranslation } from '@/features/post-translate-button/use-translation'
import { PostLayout } from '@/packages/news-ui/post/post-layout'
import { ReactionPanel } from '@/packages/news-ui/reaction-panel'
import type { Post } from '@/shared/newsdb'

import styles from './view.module.css'

type PostsContainerProps = {
    post: Post
    selectedTag: string
}

// NOTE: memoize because posts array is updated on every load more
function PostContainerBase({ post, selectedTag }: PostsContainerProps) {
    return (
        <div className={styles.newsfeedArticle}>
            <PostViewFeed post={post} selectedTag={selectedTag} />
            <div className={styles.newsFeedContentGap} />
        </div>
    )
}

export const PostMemoized = React.memo(PostContainerBase)

function PostViewFeed({ post, selectedTag }: { post: Post; selectedTag: string }) {
    const { data, handleTranslateButtonClick, isTranslated, lang } = useTranslation(post)

    return (
        <PostLayout>
            <PostLayout.Header>
                <PostTag tag={post.label} selected={post.label === selectedTag} />
                <PostTitleLink
                    title={data?.title ?? post.title}
                    showStar={post.featured}
                    label={post.label}
                    slug={post.slug}
                />
                <div className="d-flex">
                    <PostPublishingDate publishTime={post.publishTime} lang={lang} />
                    <div className="ml-auto">
                        {/* Suspense needed because PostTranslateButton uses useLoggedIn() which calls React.use() with a Promise */}
                        <React.Suspense>
                            <PostTranslateButton isTranslated={isTranslated} onClick={handleTranslateButtonClick} />
                        </React.Suspense>
                    </div>
                </div>
            </PostLayout.Header>

            <PostLayout.Content>
                <PostContent initialContent={data?.content ?? post.content} label={post.label} slug={post.slug} />
            </PostLayout.Content>

            <PostLayout.Footer>
                <ReactionPanel postId={post.id} post={post} />
            </PostLayout.Footer>
        </PostLayout>
    )
}

type PostContentProps = {
    initialContent: string
    label: string
    slug: string
}

function PostContent({ initialContent, label, slug }: PostContentProps) {
    const { hasCut, content } = parseContent({ content: initialContent, removeCut: false })

    return (
        <React.Fragment>
            <HtmlContent html={content} />
            {hasCut && <PostCut label={label} slug={slug} />}
        </React.Fragment>
    )
}
